import ready from "../behaviors/ready"

type GlobalEventMap = WindowEventMap & HTMLElementEventMap & DocumentEventMap
type EventHandler<TriggerEvent extends keyof GlobalEventMap> = (event: GlobalEventMap[TriggerEvent]) => void

export default class Trigger<TriggerEvent extends keyof GlobalEventMap> {
   private _eventType: TriggerEvent
   private _target: HTMLElement | Window | Document | null = null
   private handlers: Map<string | number, EventHandler<TriggerEvent>> = new Map()
   private pausedHandlers: Set<string | number> = new Set()
   private anonIndex: number = -1

   constructor(
      eventType: TriggerEvent,
      handler: EventHandler<TriggerEvent> | { [key: string]: EventHandler<TriggerEvent> } | EventHandler<TriggerEvent>[],
      target: string | HTMLElement | Window | Document | (() => HTMLElement | null) = 'body'
   ) {
      this._eventType = eventType
      this.add(handler)

      ready(() => {
         this.getElement(target)
         this.setup()
      })
   }

   get event(): TriggerEvent {
      return this._eventType
   }

   set event(newEventType: TriggerEvent) {
      if (this._target ? `on${newEventType}` in this._target : false) {
         this._eventType = newEventType
         this.setup()
      } else console.error(`Invalid event type: ${newEventType}`)
   }

   get target(): HTMLElement | Window | Document | null {
      return this._target
   }

   set target(el: string | HTMLElement | Window | Document | (() => HTMLElement | null)) {
      this.getElement(el).setup()
   }

   private getElement(target: string | HTMLElement | Window | Document | (() => HTMLElement | null)) {
      if (typeof target === 'string') {
         if (target === 'window') this._target = window
         else if (target === 'document') this._target = document
         else this._target = document.querySelector(target) as HTMLElement
      } else if (target instanceof HTMLElement || target instanceof Window || target instanceof Document) {
         this._target = target
      } else if (typeof target === 'function') {
         this._target = target()
      }

      return this
   }

   private setup() {
      if (this._target) {
         this.handlers.forEach(handler => this._target!.addEventListener(this._eventType, handler as EventListener))
      }
      return this
   }

   private addHandler(handler: EventHandler<TriggerEvent> | { [key: string]: EventHandler<TriggerEvent> } | EventHandler<TriggerEvent>[]) {
      const lst = []
      if (Array.isArray(handler)) {
         handler.forEach(h => {
            this.handlers.set(h.name ? h.name : this.anonIndex++, h)
            lst.push(h.name ? h.name : this.anonIndex)
         })
      } else if (typeof handler === 'object') {
         Object.entries(handler).forEach(([key, h]) => {
            this.handlers.set(key, h)
            lst.push(key)
         })
      } else {
         this.handlers.set(handler.name ? handler.name : this.anonIndex++, handler)
         lst.push(handler.name ? handler.name : this.anonIndex)
      }
      return lst
   }

   private destroy() {
      for (let key in this) (this.hasOwnProperty(key) || Object.getPrototypeOf(this).hasOwnProperty(key)) && delete this[key]
      Object.setPrototypeOf(this, null)
      return this
   }

   public add(handler: EventHandler<TriggerEvent> | { [key: string]: EventHandler<TriggerEvent> } | EventHandler<TriggerEvent>[]) {
      this.addHandler(handler)
      this.setup()
      return this
   }

   public remove(identifiers?: string | number | (string | number)[]) {
      if (identifiers === undefined) {
         this.handlers.forEach((handler, id) => this._target?.removeEventListener(this._eventType, handler as EventListener))
         this.handlers.clear()
         this.pausedHandlers.clear()
         this.destroy()
      } else {
         const ids = Array.isArray(identifiers) ? identifiers : [identifiers]
         ids.forEach(id => {
            const handler = this.handlers.get(id)
            if (this._target && handler) {
               this._target.removeEventListener(this._eventType, handler as EventListener)
               this.handlers.delete(id)
               this.pausedHandlers.delete(id)
            }
         })
      }
      return this
   }

   public toggle(identifiers?: string | number | (string | number)[], action: boolean | null = null) {
      const ids = identifiers === undefined ? Array.from(this.handlers.keys()) : (Array.isArray(identifiers) ? identifiers : [identifiers])
      ids.forEach(id => {
         const handler = this.handlers.get(id)
         if (this._target && handler) {
            if (action === null) action = this.pausedHandlers.has(id)

            if (action) {
               this._target.addEventListener(this._eventType, handler as EventListener)
               this.pausedHandlers.delete(id)
            } else {
               this._target.removeEventListener(this._eventType, handler as EventListener)
               this.pausedHandlers.add(id)
            }
         }
      })
      return null
   }

   public updateHandler(identifier: string | number | { [key: string]: EventHandler<TriggerEvent> }, newHandler?: EventHandler<TriggerEvent>) {
      if (typeof identifier === 'object' && !Array.isArray(identifier)) {
         Object.entries(identifier).forEach(([key, handler]) => this.handlers.has(key)
            && this.remove(key)
            && this.handlers.set(key, handler)
         )
      } else if (this.handlers.has(identifier as string)) {
         this.remove(identifier)
         this.handlers.set(identifier as string, newHandler!)
      }
      this.setup()

      return this
   }
}
